import { useCallback, useSyncExternalStore } from 'react';
import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  ActivityItem,
  ChatItem,
  ChatSummary,
  ChatThread,
  DownloadItem,
  DownloadsInfo,
  FriendsOverview,
  MyRating,
  RatingInput,
  Recommendation,
  SocialCounts,
} from '@teaplus/shared';
import { api } from './api';
import { useActiveProfile } from './queries';
import { looked, useFriendLooksFor, useFriendLooksVersion } from './friend-looks';

/** Your nicknames and pictures for friends, for this profile; changes when you edit one. */
export function useLooks() {
  useFriendLooksFor(useActiveProfile().data?.profile.id);
  return useFriendLooksVersion();
}

const lookActivity = (a: ActivityItem): ActivityItem => ({ ...a, profile: looked(a.profile) });

export const sk = {
  rating: (id: string) => ['rating', id] as const,
  ratings: (filter: string) => ['ratings', filter] as const,
  friends: ['friends'] as const,
  counts: ['social', 'counts'] as const,
  activity: ['friends', 'activity'] as const,
  recs: ['recommendations'] as const,
  chats: ['chats'] as const,
  // Kept apart from the list, so refreshing the list doesn't reload every conversation.
  chat: (friendId: string) => ['chat', friendId] as const,
  friendRatings: (id: string) => ['friends', 'ratings', id] as const,
  downloadsInfo: ['downloads', 'info'] as const,
  downloads: ['downloads', 'list'] as const,
};

export function useCanSocialise() {
  const d = useActiveProfile().data;
  return !!d && !d.profile.isKids && d.preferences.friendsEnabled !== false;
}

export function useCanChat() {
  const can = useCanSocialise();
  const on = useActiveProfile().data?.preferences.chatEnabled !== false;
  return can && on;
}

export const chatPath = (friendId: string) =>
  `/friends?tab=chats&with=${encodeURIComponent(friendId)}`;

/** Everyone you can chat with, newest conversation first. */
export function useChats(enabled = true) {
  const v = useLooks();
  const select = useCallback(
    (d: ChatSummary[]) => d.map((c) => ({ ...c, friend: looked(c.friend) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [v],
  );
  return useQuery({
    queryKey: sk.chats,
    queryFn: () => api<ChatSummary[]>('/api/chats'),
    select,
    enabled,
    refetchInterval: 30_000,
  });
}

export function useChatThread(friendId: string) {
  return useInfiniteQuery({
    queryKey: sk.chat(friendId),
    queryFn: ({ pageParam }) =>
      api<ChatThread>(
        `/api/chats/${friendId}/messages${pageParam ? `?before=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => (last.hasMore ? last.items[0]?.createdAt : undefined),
    refetchInterval: 60_000,
  });
}

/** Shows something just sent at the end of its chat straight away, without reloading the chat. */
export function addToChat(qc: QueryClient, friendId: string, item: ChatItem) {
  qc.setQueryData<InfiniteData<ChatThread>>(sk.chat(friendId), (d) => {
    const [newest, ...older] = d?.pages ?? [];
    if (!d || !newest || newest.items.some((i) => i.id === item.id)) return d;
    return { ...d, pages: [{ ...newest, items: [...newest.items, item] }, ...older] };
  });
}

// One message sets off several refreshes on both sides (the message, its receipts, the copy to
// your other windows). They're gathered up and sent once, so a busy chat doesn't run into the
// server's limit on requests a minute.
const GATHER_MS = 250;
let gathering: ReturnType<typeof setTimeout> | null = null;
let allThreads = false;
const threads = new Set<string>();
let deliver = false;

function gather(qc: QueryClient) {
  gathering ??= setTimeout(() => {
    gathering = null;
    void qc.invalidateQueries({ queryKey: sk.chats });
    void qc.invalidateQueries({ queryKey: sk.counts });
    if (allThreads) void qc.invalidateQueries({ queryKey: ['chat'] });
    else for (const id of threads) void qc.invalidateQueries({ queryKey: sk.chat(id) });
    if (deliver) void api('/api/chats/delivered', { method: 'POST' }).catch(() => undefined);
    allThreads = deliver = false;
    threads.clear();
  }, GATHER_MS);
}

/**
 * Refreshes the chat list, the unread counts and the chat with this friend (every open chat when
 * no friend is given), along with anything else asked for at the same moment.
 */
export function refreshChats(qc: QueryClient, friendId?: string) {
  if (friendId) threads.add(friendId);
  else allThreads = true;
  gather(qc);
}

/** Tells the server this app has what was sent, for the sender's Delivered tick. */
export function markChatsDelivered(qc: QueryClient) {
  deliver = true;
  gather(qc);
}

/** The chat on screen right now, or 'list' for the Chats tab with none open. */
let openChat: string | null = null;
export function setOpenChat(where: string | null) {
  openChat = where;
}
/** True when something new from this friend is already in front of you: no notification needed. */
export function isChatOpen(friendId: string) {
  return (openChat === friendId || openChat === 'list') && document.visibilityState === 'visible';
}
/** One card per friend: a new message replaces the last, and opening the chat clears it. */
export const chatToastId = (friendId: string) => `chat:${friendId}`;

export function useRatingsOn() {
  return useActiveProfile().data?.preferences.ratingsEnabled !== false;
}

export function useRating(mediaId: string) {
  return useQuery({
    queryKey: sk.rating(mediaId),
    queryFn: () => api<{ rating: MyRating | null }>(`/api/ratings/${mediaId}`),
    enabled: !!mediaId,
  });
}

export function useSaveRating(mediaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RatingInput) =>
      api<{ rating: MyRating | null }>(`/api/ratings/${mediaId}`, { method: 'PUT', body: input }),
    onSuccess: (data) => {
      qc.setQueryData(sk.rating(mediaId), data);
      qc.invalidateQueries({ queryKey: ['ratings'] });
      qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
}

export function useRatings(filter: 'all' | 'favorites' | 'rated', enabled = true) {
  return useQuery({
    queryKey: sk.ratings(filter),
    queryFn: () => api<MyRating[]>(`/api/ratings?filter=${filter}`),
    enabled,
  });
}

export function useFriends(enabled = true) {
  const v = useLooks();
  const select = useCallback(
    (d: FriendsOverview): FriendsOverview => ({
      ...d,
      friends: d.friends.map((f) => ({
        ...looked(f),
        watching: f.watching && lookActivity(f.watching),
      })),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [v],
  );
  return useQuery({
    queryKey: sk.friends,
    queryFn: () => api<FriendsOverview>('/api/friends'),
    select,
    enabled,
    refetchInterval: 30_000,
  });
}

export function useSocialCounts() {
  const can = useCanSocialise();
  return useQuery({
    queryKey: sk.counts,
    queryFn: () => api<SocialCounts>('/api/social/counts'),
    enabled: can,
    refetchInterval: 60_000,
  });
}

export function useActivity(enabled = true) {
  const v = useLooks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const select = useCallback((d: ActivityItem[]) => d.map(lookActivity), [v]);
  return useQuery({
    queryKey: sk.activity,
    queryFn: () => api<ActivityItem[]>('/api/friends/activity'),
    select,
    enabled,
    refetchInterval: 30_000,
  });
}

export function useRecommendations(enabled = true) {
  const v = useLooks();
  const select = useCallback(
    (d: Recommendation[]) => d.map((r) => ({ ...r, from: looked(r.from) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [v],
  );
  return useQuery({
    queryKey: sk.recs,
    queryFn: () => api<Recommendation[]>('/api/recommendations'),
    select,
    enabled,
  });
}

export function useFriendRatings(mediaId: string) {
  const can = useCanSocialise();
  const v = useLooks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const select = useCallback((d: ActivityItem[]) => d.map(lookActivity), [v]);
  return useQuery({
    queryKey: sk.friendRatings(mediaId),
    queryFn: () => api<ActivityItem[]>(`/api/friends/ratings/${mediaId}`),
    select,
    enabled: can && !!mediaId,
  });
}

export function useDownloadsInfo() {
  return useQuery({
    queryKey: sk.downloadsInfo,
    queryFn: () => api<DownloadsInfo>('/api/local/downloads/info'),
    retry: false,
    staleTime: 60_000,
  });
}

export function useDownloads(enabled = true) {
  return useQuery({
    queryKey: sk.downloads,
    queryFn: () => api<DownloadItem[]>('/api/local/downloads'),
    enabled,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((d) => d.status === 'downloading' || d.status === 'queued')
        ? 1000
        : false,
  });
}

const seenKey = (profileId: string) => `tp:activity-seen:${profileId}`;
let seenTick = 0;
const seenListeners = new Set<() => void>();

/** When this profile last looked at friends' activity (first use counts as now). */
function activitySeenAt(profileId: string) {
  try {
    const v = Number(localStorage.getItem(seenKey(profileId)));
    if (v) return v;
    localStorage.setItem(seenKey(profileId), String(Date.now()));
  } catch {}
  return Date.now();
}

export function markActivitySeen(profileId: string) {
  try {
    localStorage.setItem(seenKey(profileId), String(Date.now()));
  } catch {}
  seenTick++;
  seenListeners.forEach((l) => l());
}

/** Friends' activity since you last opened Friends, for the badge on the Friends button. */
export function useUnseenActivity() {
  const can = useCanSocialise();
  const profileId = useActiveProfile().data?.profile.id;
  const activity = useActivity(can);
  useSyncExternalStore(
    (cb) => {
      seenListeners.add(cb);
      return () => seenListeners.delete(cb);
    },
    () => seenTick,
  );
  if (!can || !profileId || !activity.data) return 0;
  const since = activitySeenAt(profileId);
  return activity.data.filter((a) => Date.parse(a.at) > since).length;
}

/** Asks a friend who is watching something to turn it into a party with you. */
export async function requestToJoin(friend: { id: string; name: string }) {
  await api<{ requestId: string }>('/api/party/requests', { body: { to: friend.id } });
}
